import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    CircularProgress,
    TextField,
    InputAdornment,
    Grid,
    Card,
    Avatar
} from '@mui/material';
import { Search as SearchIcon, Visibility as VisibilityIcon, LocalShipping, Moped, Schedule, CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAdminDispatches } from 'hooks/useDispatches';
import { format } from 'date-fns';

export default function DispatchList() {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');

    const { data: dispatchesRes, isLoading } = useAdminDispatches();
    const dispatches = Array.isArray(dispatchesRes) ? dispatchesRes : dispatchesRes?.data || [];

    const filteredDispatches = dispatches.filter((dispatch) => {
        const orderId = dispatch.order?._id?.toLowerCase() || '';
        const riderName = `${dispatch.rider?.firstName} ${dispatch.rider?.lastName}`.toLowerCase();
        return orderId.includes(searchTerm.toLowerCase()) || riderName.includes(searchTerm.toLowerCase());
    });

    const getStatusChip = (status) => {
        switch (status) {
            case 'pending':
                return <Chip label="Searching Rider" color="warning" size="small" />;
            case 'accepted':
                return <Chip label="Accepted" color="info" size="small" />;
            case 'on-the-way-to-pickup':
                return <Chip label="To Pickup" color="info" size="small" />;
            case 'arrived-at-pickup':
                return <Chip label="At Pickup" color="primary" size="small" />;
            case 'out-for-delivery':
                return <Chip label="In Transit" color="primary" size="small" />;
            case 'delivered':
                return <Chip label="Completed" color="success" size="small" />;
            case 'cancelled':
                return <Chip label="Cancelled" color="error" size="small" />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    const getKpiData = () => {
        const active = dispatches.filter((d) => ['accepted', 'out-for-delivery', 'on-the-way-to-pickup'].includes(d.status)).length;
        const pending = dispatches.filter((d) => d.status === 'pending').length;
        const completed = dispatches.filter((d) => d.status === 'delivered').length;
        return { active, pending, completed };
    };

    const kpis = getKpiData();

    return (
        <Box p={3} sx={{ background: '#f5f6fa', minHeight: '100vh' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>
                        Logistics Monitor
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Real-time tracking of active dispatches and rider productivity
                    </Typography>
                </Box>
            </Stack>

            <Grid container spacing={3} mb={4}>
                <KpiCard title="Active Trips" value={kpis.active} icon={<LocalShipping color="primary" />} color="#1976d2" />
                <KpiCard title="Pending Pickup" value={kpis.pending} icon={<Schedule color="warning" />} color="#ed6c02" />
                <KpiCard title="Completed Today" value={kpis.completed} icon={<CheckCircle color="success" />} color="#2e7d32" />
            </Grid>

            {/* Table */}
            <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                <Box p={3}>
                    <TextField
                        placeholder="Search by Order ID or Rider Name..."
                        size="small"
                        fullWidth
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ maxWidth: 400 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, pl: 3 }}>ORDER REF</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>RIDER</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>STATUS</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>STARTED AT</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, pr: 3 }}>
                                    ACTIONS
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" py={5}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : filteredDispatches.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" py={5}>
                                        <Typography variant="body2" color="text.secondary">
                                            No active dispatches found matching your search.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredDispatches.map((dispatch) => (
                                    <TableRow key={dispatch._id} hover>
                                        <TableCell sx={{ pl: 3 }}>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                                #{dispatch.order?.shortId || dispatch.order?._id?.substring(0, 8)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {dispatch.order?.type || 'Standard Delivery'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {dispatch.rider ? (
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <Avatar
                                                        src={dispatch.rider.profilePicture}
                                                        sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}
                                                    >
                                                        <Moped fontSize="small" />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {dispatch.rider.firstName} {dispatch.rider.lastName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {dispatch.rider.phone}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            ) : (
                                                <Typography variant="caption" color="error">
                                                    Unassigned
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{getStatusChip(dispatch.status)}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {dispatch.createdAt ? format(new Date(dispatch.createdAt), 'HH:mm • MMM dd') : '---'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ pr: 3 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/admin/orders/${dispatch.order?._id}`)}
                                                sx={{ border: '1px solid #eee' }}
                                            >
                                                <VisibilityIcon fontSize="small" color="primary" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
}

function KpiCard({ title, value, icon, color }) {
    return (
        <Grid item xs={12} sm={4}>
            <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {title.toUpperCase()}
                        </Typography>
                        <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>
                            {value}
                        </Typography>
                    </Box>
                    <Box sx={{ bgcolor: `${color}15`, p: 1.5, borderRadius: 2 }}>{icon}</Box>
                </Stack>
            </Card>
        </Grid>
    );
}
